import { VALUE_UPDATE_EVENT } from "../../lib/constants.js";
import { deserializeControlValue, serializeControlValue } from "../../lib/control.js";
import { parseDocValue } from "../../lib/parse.js";
import { computed, createBlock, createCommentVNode, createElementBlock, createTextVNode, createVNode, mergeProps, normalizeClass, openBlock, ref, resolveComponent, resolveDirective, resolveDynamicComponent, toDisplayString, unref, watch, withCtx, withDirectives } from "vue";
import DtIconLock from "@dialpad/dialtone-icons/vue/lock";
import { DtButton, DtInput, DtText } from "@dialpad/dialtone-vue";
import JSON5 from "json5-with-undefined";
//#region src/components/option_bar/option_bar_control.vue
var _sfc_main = /* @__PURE__ */ Object.assign({ name: "DtcOptionBarControl" }, {
	props: {
		controlData: {
			type: Object,
			required: true
		},
		validControls: {
			type: Array,
			required: true
		},
		value: {
			type: void 0,
			required: true
		},
		label: {
			type: String,
			required: true
		},
		description: {
			type: String,
			default: void 0
		},
		vModel: {
			type: Boolean,
			default: false
		},
		required: {
			type: Boolean,
			default: false
		},
		locked: {
			type: Boolean,
			default: false
		},
		disabled: {
			type: Boolean,
			default: false
		},
		args: {
			type: Object,
			default: () => {}
		}
	},
	emits: [VALUE_UPDATE_EVENT],
	setup(__props, { emit: __emit }) {
		const props = __props;
		const emit = __emit;
		const controlValue = computed(() => {
			return props.controlData.serialize ? serializeControlValue(props.value) : props.value;
		});
		const controlLabel = computed(() => props.label.replaceAll("-", " "));
		const controlComponent = computed(() => {
			return props.controlData.component;
		});
		/**
		* Args that are conditionally passed to the
		* underlying control props if the prop is present on the control.
		*
		* @type {ComputedRef<object>}
		*/
		const controlArgs = computed(() => {
			return {
				value: props.disabled && !props.locked ? props.controlData.component.props?.value?.default?.() ?? controlValue.value : controlValue.value,
				disabled: props.locked || props.disabled,
				tags: props.tags,
				...props.args
			};
		});
		/**
		* Object containing only the args that are
		* present on the control component props.
		*
		* @type {ComputedRef<object>}
		*/
		const controlBindings = computed(() => {
			const component = controlComponent.value;
			if (!component.props) return null;
			return Object.fromEntries(Object.entries(controlArgs.value).filter(([arg]) => {
				return Object.keys(component.props).includes(arg);
			}));
		});
		/**
		* Emits an update to the member value.
		* The value is deserialized if needed.
		*
		* @param e - The updated member value
		*/
		function updateValue(e) {
			emit(VALUE_UPDATE_EVENT, props.controlData.serialize ? deserializeControlValue(e) : e);
		}
		const showRawToggle = computed(() => {
			const name = props.controlData.component?.name;
			return name === "DtcControlArray" || name === "DtcControlObject";
		});
		const rawMode = ref(false);
		const rawText = ref("");
		let rawEditInProgress = false;
		function formatRawValue(val) {
			return JSON5.stringify(val, null, 2);
		}
		watch(() => props.value, (val) => {
			if (rawMode.value && !rawEditInProgress) rawText.value = formatRawValue(val);
		}, { deep: true });
		function toggleRawMode() {
			rawMode.value = !rawMode.value;
			if (rawMode.value) rawText.value = formatRawValue(props.value);
		}
		watch(rawText, (val) => {
			try {
				rawEditInProgress = true;
				emit(VALUE_UPDATE_EVENT, parseDocValue(val));
			} catch {} finally {
				rawEditInProgress = false;
			}
		});
		return (_ctx, _cache) => {
			const _component_dt_stack = resolveComponent("dt-stack");
			const _directive_dt_tooltip = resolveDirective("dt-tooltip");
			return openBlock(), createElementBlock("div", null, [(openBlock(), createBlock(resolveDynamicComponent(rawMode.value ? "dt-text" : controlComponent.value), mergeProps(rawMode.value ? {
				as: "div",
				kind: "label",
				size: "xs",
				tone: "secondary"
			} : controlBindings.value, { "onUpdate:value": updateValue }), {
				default: withCtx(() => [createVNode(_component_dt_stack, {
					as: "div",
					direction: "row",
					gap: "300",
					justify: "space-between",
					align: "baseline"
				}, {
					default: withCtx(() => [createVNode(_component_dt_stack, {
						direction: "row",
						gap: "300",
						align: "baseline"
					}, {
						default: withCtx(() => [
							withDirectives((openBlock(), createBlock(unref(DtText), {
								as: "span",
								class: "d-tt-capitalize",
								tone: __props.disabled ? "muted" : void 0,
								"data-qa": "dtc-option-bar-control-label"
							}, {
								default: withCtx(() => [createTextVNode(toDisplayString(controlLabel.value), 1)]),
								_: 1
							}, 8, ["tone"])), [[_directive_dt_tooltip, {
								message: __props.description,
								placement: "left"
							}]]),
							__props.locked ? (openBlock(), createBlock(unref(DtIconLock), {
								key: 0,
								size: "100"
							})) : createCommentVNode("", true),
							__props.required ? (openBlock(), createBlock(unref(DtText), {
								key: 1,
								size: 100,
								kind: "label",
								strength: "normal",
								tone: "critical",
								class: "d-fs-50"
							}, {
								default: withCtx(() => [..._cache[1] || (_cache[1] = [createTextVNode(" Required ", -1)])]),
								_: 1
							})) : createCommentVNode("", true),
							__props.vModel ? (openBlock(), createBlock(unref(DtText), {
								key: 2,
								size: 100,
								kind: "label",
								strength: "normal",
								tone: "muted",
								class: "d-fs-50"
							}, {
								default: withCtx(() => [..._cache[2] || (_cache[2] = [createTextVNode(" v-model ", -1)])]),
								_: 1
							})) : createCommentVNode("", true)
						]),
						_: 1
					}), showRawToggle.value ? withDirectives((openBlock(), createBlock(unref(DtButton), {
						key: 0,
						link: "",
						"link-underline": false,
						class: normalizeClass(["d-mis-auto d-fw-normal d-fs-50 d-px-25 d-bar-200 h:d-td-none", { "d-bgc-bold d-fc-secondary h:d-fc-primary": rawMode.value }]),
						onClick: toggleRawMode
					}, {
						default: withCtx(() => [..._cache[3] || (_cache[3] = [createTextVNode(" RAW ", -1)])]),
						_: 1
					}, 8, ["class"])), [[_directive_dt_tooltip, "Edit as JSON"]]) : createCommentVNode("", true)]),
					_: 1
				}), rawMode.value ? (openBlock(), createBlock(unref(DtInput), {
					key: 0,
					modelValue: rawText.value,
					"onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => rawText.value = $event),
					type: "textarea",
					size: 100,
					spellcheck: "false",
					class: "d-mbs-75"
				}, null, 8, ["modelValue"])) : createCommentVNode("", true)]),
				_: 1
			}, 16))]);
		};
	}
});
//#endregion
export { _sfc_main as default };

//# sourceMappingURL=option_bar_control.vue.js.map